import { ApiErrorEnum } from '../../enums/ApiErrorEnum';

const API_ERROR_CODE_PATTERN = /^[A-Z][A-Z0-9_]+$/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function unwrapApiErrorPayload(response: unknown): Record<string, unknown> | undefined {
  if (!isPlainObject(response)) {
    return undefined;
  }

  const record = response as Record<string, unknown>;
  const nestedResponse = record.response;

  if (isPlainObject(nestedResponse)) {
    if (isPlainObject(nestedResponse.data)) {
      return nestedResponse.data;
    }

    return nestedResponse;
  }

  if (isPlainObject(record.data)) {
    return record.data;
  }

  return record;
}

/**
 * Extracts a backend API error code from an HTTP error `response` payload.
 * Supports plain strings and common object shapes (`error_code`, `name`, `error`, `code`, `why`, `whyError`, `Name`).
 * `error_code` is the canonical `ApiError` shape used by new and migrated endpoints.
 */
export function getApiErrorCode(response: unknown): string | undefined {
  if (typeof response === 'string') {
    const trimmed = response.trim().replace(/^"+|"+$/g, '');
    return trimmed.length > 0 ? trimmed : undefined;
  }

  const record = unwrapApiErrorPayload(response);
  if (!record) {
    return undefined;
  }

  if (typeof record.error_code === 'string') {
    return record.error_code;
  }
  if (typeof record.name === 'string') {
    return record.name;
  }
  if (typeof record.error === 'string') {
    return record.error;
  }
  if (typeof record.code === 'string') {
    return record.code;
  }
  if (typeof record.why === 'string') {
    return record.why;
  }
  if (typeof record.whyError === 'string') {
    return record.whyError;
  }
  if (typeof record.Name === 'string') {
    return record.Name;
  }
  if (typeof record.message === 'string' && API_ERROR_CODE_PATTERN.test(record.message)) {
    return record.message;
  }

  return undefined;
}

/** Returns true when `response` resolves to the given `ApiErrorEnum` code. */
export function isApiError(response: unknown, code: ApiErrorEnum): boolean {
  return getApiErrorCode(response) === code;
}

/**
 * Extracts the accumulated validation errors of a `SHAREPOINT_CONFIG_INVALID` payload.
 *
 * The backend validator deliberately accumulates instead of stopping at the first failure, so every
 * entry must be shown — never just the first. Returns `undefined` when the payload carries no
 * `problems` array (the shape may be `{ detail }` instead: fall back to `getApiErrorDetail`).
 */
export function getApiErrorProblems(response: unknown): string[] | undefined {
  const record = unwrapApiErrorPayload(response);
  if (!record || !Array.isArray(record.problems)) {
    return undefined;
  }

  const problems = record.problems
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item !== '');

  return problems.length > 0 ? problems : undefined;
}

/**
 * Reads an optional string field from an `ApiError` payload (e.g. `status`, `operation`).
 * Numbers are coerced to string (e.g. share `fileId: 123`).
 */
export function getApiErrorField(response: unknown, field: string): string | undefined {
  const record = unwrapApiErrorPayload(response);
  if (!record) {
    return undefined;
  }
  const value = record[field];
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
}

/**
 * Extracts human-readable backend detail from an HTTP error payload.
 * Supports standard `{ detail: [] }`, plus legacy `value`, `message`, and `error`.
 */
export function getApiErrorDetail(response: unknown): string | undefined {
  const record = unwrapApiErrorPayload(response);
  if (!record) {
    return undefined;
  }

  if (typeof record.detail === 'string') {
    return record.detail.trim() || undefined;
  }

  if (Array.isArray(record.detail)) {
    const detailText = record.detail
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean)
      .join('\n');

    if (detailText) {
      return detailText;
    }
  }

  if (typeof record.value === 'string' && record.value.trim()) {
    return record.value.trim();
  }

  if (typeof record.message === 'string' && record.message.trim()) {
    return record.message.trim();
  }

  if (typeof record.error === 'string' && record.error.trim()) {
    return record.error.trim();
  }

  return undefined;
}
